// Iter274c — Acceptance status badge + operator action buttons for
// each contractor-type section in the Contractor Overview.
import React from 'react';
import { toast } from 'sonner';

const STATUS_META = {
  pending:     { label: 'Pending acceptance', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  accepted:    { label: 'Accepted',           cls: 'bg-green-100 text-green-800 border-green-300' },
  rejected:    { label: 'Rejected',           cls: 'bg-red-100 text-red-800 border-red-300' },
  work_booked: { label: 'Work Booked',        cls: 'bg-blue-100 text-blue-800 border-blue-300' },
};

const REJECTION_CATEGORY_LABEL = {
  too_busy: 'Too busy',
  skill_mismatch: 'Skills mismatch',
  need_more_labour: 'Need more labour',
  scope_changes: 'Scope changes',
  pricing: 'Pricing',
  other: 'Other',
};

export const AcceptanceBadge = ({
  ctype,
  acceptance,
  apiBaseUrl,
  appraisalId,
  api,
  onChange,
}) => {
  const status = (acceptance?.status || 'pending').toLowerCase();
  const meta = STATUS_META[status] || STATUS_META.pending;
  const has_token = !!acceptance?.token;

  const copyLink = async () => {
    if (!has_token) {
      toast.error('No acceptance link yet — click "Generate links" first');
      return;
    }
    const url = `${apiBaseUrl}/api/contract/respond?t=${acceptance.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Acceptance link copied for ${ctype}`);
    } catch {
      window.prompt(`Copy this link for ${ctype}:`, url);
    }
  };

  const markManual = async (action) => {
    let body = { action };
    if (action === 'reject') {
      const reason = window.prompt(`Reason for rejecting ${ctype}? (You can type free text — category defaults to "other")`);
      if (reason === null) return;
      body.rejection_reason = reason;
      body.rejection_category = 'other';
    }
    body.responder_name = `Operator (manual)`;
    try {
      await api.post(`/appraisals/${appraisalId}/acceptance/${ctype}/manual`, body);
      toast.success(`Marked ${ctype} as ${action === 'accept' ? 'Accepted' : action === 'reject' ? 'Rejected' : 'Pending'}`);
      onChange && onChange();
    } catch (err) {
      toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const date = (acceptance?.responded_at || '').slice(0, 10);
  const by   = acceptance?.responder_name || '';
  const reason = acceptance?.rejection_reason || '';
  const category = acceptance?.rejection_category || '';

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`acceptance-badge-${ctype}`}>
      <span
        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}
        title={
          status === 'accepted' ? `Accepted on ${date} by ${by}` :
          status === 'rejected' ? `Rejected on ${date} by ${by} — ${REJECTION_CATEGORY_LABEL[category] || ''}: ${reason}` :
          status === 'pending'  ? (has_token ? `Awaiting contractor response. Link generated.` : `No link yet — click "Generate links"`) :
          `Status: ${status}`
        }
      >
        {meta.label}{(status === 'accepted' || status === 'rejected') && by ? ` · ${by}` : ''}
      </span>
      {has_token && status === 'pending' && (
        <button
          data-testid={`acceptance-copy-link-${ctype}`}
          onClick={copyLink}
          className="text-[10px] font-bold px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          title="Copy the contractor's direct response link to clipboard"
        >📋 Copy link</button>
      )}
      {status === 'pending' && (
        <>
          <button
            data-testid={`acceptance-mark-accept-${ctype}`}
            onClick={() => markManual('accept')}
            className="text-[10px] font-bold px-2 py-0.5 rounded border border-green-300 bg-white text-green-700 hover:bg-green-50"
            title="Manually mark this contract as accepted on behalf of the contractor"
          >✓ Accept</button>
          <button
            data-testid={`acceptance-mark-reject-${ctype}`}
            onClick={() => markManual('reject')}
            className="text-[10px] font-bold px-2 py-0.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50"
            title="Manually mark this contract as rejected (you'll be asked for a reason)"
          >✗ Reject</button>
        </>
      )}
      {(status === 'accepted' || status === 'rejected') && (
        <button
          data-testid={`acceptance-reset-${ctype}`}
          onClick={() => markManual('pending')}
          className="text-[10px] font-bold px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
          title="Reset this acceptance back to Pending (e.g. operator made a mistake)"
        >↺ Reset</button>
      )}
    </div>
  );
};
